import { useQuery } from '@apollo/client';
import {
  Modal,
  ModalSize,
  PrimaryButton,
  Checkbox,
  Flex,
  OSmallParagraph,
  OHeaderThree,
  Box,
  OParagraph,
  MagnifyingGlassIcon,
  Input,
  Badge,
  BadgeType,
} from '@lifechurch/react-ion';
import dayjs from 'dayjs';
import React, { FC, ReactElement, useState } from 'react';
import styled from 'styled-components';
import { searchByTitleAndContent } from '../Moments';
import { getTitle, getContent } from '@lib/moments';
import { MOMENT_TEMPLATES_QUERY } from '@lib/queries';
import { defaultColors } from '@lifechurch/react-ion';
import {
  MomentScheduleItemFieldsFragment,
  MomentTemplateFieldsFragment,
  MomentTemplatesQuery,
  MomentType,
} from '@generated/types';
import { useTranslation } from 'react-i18next';

interface Props {
  addMomentScheduleItems: (addMomentScheduleItems: Array<MomentScheduleItemFieldsFragment>) => void;
  momentScheduleItems: Array<MomentScheduleItemFieldsFragment>;
  options?: {
    [MomentType.PRAYER]: {
      isLivePrayerEnabled: boolean;
    };
  };
  dismiss: () => void;
}

interface ItemWrapperProps {
  isDisabled: boolean;
}

const ItemWrapper = styled(Flex)<ItemWrapperProps>`
  color: ${props => props.isDisabled && props.theme.colors.gray50};

  &:hover {
    background-color: ${props => !props.isDisabled && props.theme.colors.action.light};
  }

  &:not(:last-child) {
    margin-bottom: 2px;
  }
`;

const Quantity = styled.span`
  color: ${defaultColors.gray30};
`;

const AddMomentsToContentModal: FC<Props> = ({
  addMomentScheduleItems,
  dismiss,
  momentScheduleItems,
  options,
}) => {
  const { data } = useQuery<MomentTemplatesQuery>(MOMENT_TEMPLATES_QUERY);
  const [selectedMoments, setSelectedMoments] = useState<Array<MomentScheduleItemFieldsFragment>>(
    []
  );
  const [searchValue, setSearchValue] = useState<string>('');
  const { t } = useTranslation('add_moments_to_content_modal');

  const getQuantity = (momentTemplate: MomentTemplateFieldsFragment): number =>
    momentScheduleItems.filter(
      momentScheduleItem => momentScheduleItem.momentTemplate.id === momentTemplate.id
    ).length;

  const shouldDisable = (type: MomentType): boolean => {
    switch (type) {
      case MomentType.PRAYER:
        return (
          (options &&
            options[MomentType.PRAYER] &&
            !options[MomentType.PRAYER].isLivePrayerEnabled) ||
          false
        );
      default:
        return false;
    }
  };

  const momentIsSelected = (id: string): boolean =>
    !!selectedMoments.find(moment => moment.momentTemplate.id === id);

  const handleChange = (momentTemplate: MomentTemplateFieldsFragment): void => {
    const isSelected = momentIsSelected(momentTemplate.id);
    if (shouldDisable(momentTemplate.type)) {
      return;
    }

    if (isSelected) {
      setSelectedMoments(
        selectedMoments.filter(moment => moment.momentTemplate.id !== momentTemplate.id)
      );
    } else {
      setSelectedMoments([
        ...selectedMoments,
        {
          id: '',
          momentTemplate,
          startTimeOffset: null,
          ordinal: null,
        },
      ]);
    }
  };

  const handleClick = (event: React.MouseEvent): void => event.preventDefault();

  const renderMoment = (momentTemplate: MomentTemplateFieldsFragment): ReactElement => (
    <ItemWrapper
      alignItems='center'
      bg='gray5'
      borderRadius={1}
      isDisabled={shouldDisable(momentTemplate.type)}
      key={momentTemplate.id}
      onClick={() => handleChange(momentTemplate)}
      p={5}
    >
      <Checkbox
        checked={momentIsSelected(momentTemplate.id)}
        disabled={shouldDisable(momentTemplate.type)}
        name={momentTemplate.id}
        onChange={() => {
          // We want this event to bubble to the parent div
          return;
        }}
      >
        <OParagraph mx={5} onClick={handleClick} width={100}>
          {getTitle(momentTemplate.type)}
          {getQuantity(momentTemplate) > 0 && (
            <Quantity>{` (${getQuantity(momentTemplate)})`}</Quantity>
          )}
        </OParagraph>
      </Checkbox>
      <Box flex={1} marginInlineEnd={5} overflow='hidden'>
        <OSmallParagraph
          color={shouldDisable(momentTemplate.type) ? defaultColors.gray50 : defaultColors.gray80}
          style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {getContent(momentTemplate)}
        </OSmallParagraph>
      </Box>
      {shouldDisable(momentTemplate.type) && (
        <Badge marginInlineEnd={5} type={BadgeType.INFORMATION}>
          {t('prayer_must_be_enabled')}
        </Badge>
      )}
      <OSmallParagraph
        color={shouldDisable(momentTemplate.type) ? defaultColors.gray50 : defaultColors.gray80}
        overflow='hidden'
        width={110}
      >
        {t('edited', { time: dayjs(momentTemplate.updatedAt).format('l') })}
      </OSmallParagraph>
    </ItemWrapper>
  );

  const handleAdd = (): void => {
    addMomentScheduleItems(selectedMoments);
    dismiss();
  };

  return (
    <Modal dismiss={dismiss} size={ModalSize.WIDE}>
      <OHeaderThree marginBlockEnd={5}>{t('choose_a_moment')}</OHeaderThree>
      <Flex marginBlockEnd={5}>
        <Box position='relative' width='250px'>
          <Box marginInlineStart={4} position='absolute' top='10px'>
            <MagnifyingGlassIcon />
          </Box>
          <Input
            onChange={e => setSearchValue(e.target.value)}
            paddingInlineStart={7}
            placeholder={t('search')}
            value={searchValue}
          />
        </Box>
        <Box marginInlineStart='auto'>
          <PrimaryButton disabled={selectedMoments.length === 0} onClick={handleAdd}>
            {selectedMoments.length === 1 ? t('add_moment') : t('add_moments')}
          </PrimaryButton>
        </Box>
      </Flex>
      <Box minHeight={214}>
        {data?.currentOrganization?.momentTemplates
          .filter(momentTemplate =>
            searchValue ? searchByTitleAndContent(momentTemplate, searchValue) : true
          )
          .map(momentTemplate => renderMoment(momentTemplate))}
      </Box>
    </Modal>
  );
};

export default AddMomentsToContentModal;
